/**
 * @file revealInSearch.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-07
 * @modified 2026-10-01
 * @description "Show this row in the search screen it came from."
 *
 *              The entity modals open from anywhere — a chip on a bill, a
 *              number in the contact modal, a linked order — and once open they
 *              are a dead end: the record is in front of you with no way to get
 *              to the screen that lists it, where the filters, the bulk actions
 *              and the neighbouring rows are.
 *
 *              A handoff through sessionStorage rather than a prop chain: the
 *              modal is unmounted by the navigation it triggers, so there is no
 *              component alive to hold the value across it. The receiving
 *              screen mounts, takes it, and clears it — so a back-navigation
 *              later does not silently re-apply a search the user has moved on
 *              from.
 *
 *              This is the ONE way to send a user to a search screen with
 *              something searched for: the modals' "Find in …", Inventory's
 *              cross-search, the contact panels' open-in-search and the "open
 *              these POs" ways out of the order screens all come through here.
 *
 * HOW A RECEIVING SCREEN TAKES IT (v1.1.0)
 *
 *     const reveal = useReveal('search-customer-orders');
 *     const [pagination, setPagination] = usePersistedState('x.pagination', …,
 *       { seed: reveal && { page: 1 } });
 *     const [filters, setFilters] = usePersistedState('x.filters', …,
 *       { merge: true, seed: revealFilters(reveal) });
 *
 *   The term goes INTO the first render's state, and the page goes back to 1.
 *   It used to be applied by a setFilters in a mount effect, "so it takes the
 *   same path a typed search does" — but the screen's mount search runs in that
 *   same commit, reading the filters of the render BEFORE the term arrived, and
 *   the search-on-change effect that should have followed is held off for the
 *   first 350ms of a mount. So the box showed the new reference and the table
 *   showed the old search; only deleting and retyping a character (after the
 *   350ms) ran it. And the page was the one the user had last been on — page 3
 *   of a search that now has one result is an empty table.
 */

import { useEffect, useState } from 'react';

const REVEAL_KEY = 'revealInSearch';

/*
 * How long a handoff waits for its screen. Navigation takes 300ms (the
 * desktop crossfade), so anything still waiting after this never arrived — the
 * page it was meant for did not mount — and applying it on the next, unrelated
 * visit would put a search in the box the user never asked for there.
 */
const REVEAL_TTL_MS = 15000;

/*
 * The same handoff, kept in memory as well. Navigation is in-page — the
 * sender and the receiver live in the same JS context — so this is all a
 * handoff strictly needs; sessionStorage is the copy that survives a reload
 * mid-navigation. Without it a refused setItem (a store at its quota) sent the
 * user to the screen with the OLD search on the old page, which is the bug
 * this module exists to prevent. Whichever copy is newer wins, so a refused
 * write can never surface an older stored handoff. (The nav store persists to
 * the same sessionStorage and does not catch a refused write, so a store that
 * refuses everything breaks more than this; this covers the handoff's part.)
 */
let memoryHandoff = null;

function readStored() {
  try {
    const raw = sessionStorage.getItem(REVEAL_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function removeStored(at) {
  try {
    if (at === undefined) { sessionStorage.removeItem(REVEAL_KEY); return; }
    const stored = readStored();
    if (stored && stored.at === at) sessionStorage.removeItem(REVEAL_KEY);
  } catch (_) {
    // Nothing we can clear — the TTL retires it anyway.
  }
}

/** The newest pending handoff, from memory or storage, or null. */
function readHandoff() {
  const stored = readStored();
  const memory = memoryHandoff;
  if (!stored) return memory;
  if (!memory) return stored;
  return Number(memory.at || 0) >= Number(stored.at || 0) ? memory : stored;
}

/**
 * Send the user to `menu` with `term` in its search box.
 *
 * @param {Function} onNavigate  the shell's navigateToMenu (menu, params)
 * @param {string}   menu        the target screen's menu id
 * @param {string}   term        the reference to search for
 * @param {Function} [onClose]   close the modal first; navigating out from
 *                               under an open modal leaves it covering the
 *                               screen it just sent you to
 * @param {Object}   [options]
 * @param {string}   [options.tab]      the tab of `menu` the list lives on —
 *                                      without it a page left on another tab
 *                                      (Sales Orders → Pricing) is navigated
 *                                      to, stays on that tab, and the list
 *                                      never mounts to take the handoff
 * @param {Object}   [options.filters]  other filter values to arrive with,
 *                                      laid over the screen's saved ones
 *                                      (e.g. "include deleted", "show void")
 * @returns {boolean} true when it navigated
 */
export function revealInSearch(onNavigate, menu, term, onClose, { tab, filters } = {}) {
  const value = String(term ?? '').trim();
  if (!menu || !value || typeof onNavigate !== 'function') return false;
  const handoff = {
    menu,
    term: value,
    filters: filters && typeof filters === 'object' ? filters : null,
    at: Date.now()
  };
  memoryHandoff = handoff;
  try {
    sessionStorage.setItem(REVEAL_KEY, JSON.stringify(handoff));
  } catch (_) {
    // Private mode, or a full store: the in-memory copy carries it (see
    // memoryHandoff). Drop any older stored one so it cannot outlive this.
    removeStored();
  }
  if (typeof onClose === 'function') onClose();
  onNavigate(menu, tab ? { tab } : undefined);
  return true;
}

/** The pending handoff for this screen, without removing it. */
export function peekReveal(menu) {
  const parsed = readHandoff();
  if (!parsed) return null;
  if (!(Date.now() - Number(parsed.at || 0) <= REVEAL_TTL_MS)) {
    // Never arrived — see REVEAL_TTL_MS. Older handoffs carry no `at` and
    // are dropped the same way.
    clearReveal(parsed);
    return null;
  }
  if (parsed.menu !== menu || !parsed.term) return null;
  return {
    term: String(parsed.term),
    filters: parsed.filters && typeof parsed.filters === 'object' ? parsed.filters : null,
    at: parsed.at
  };
}

/** Remove a handoff once it has been applied — only that one, never a newer. */
export function clearReveal(reveal) {
  if (!reveal) return;
  if (memoryHandoff && memoryHandoff.at === reveal.at) memoryHandoff = null;
  removeStored(reveal.at);
}

/** Drop any pending handoff, whatever screen it was for (both copies). */
export function dropReveal() {
  memoryHandoff = null;
  removeStored();
}

/**
 * The handoff for this screen, read once when the screen mounts and cleared
 * once it has. Call it BEFORE the screen's usePersistedState calls, so the
 * filters and page can be seeded from it.
 *
 * Read in a lazy initialiser (not on every render), and cleared in an effect
 * rather than while reading: React may call an initialiser twice and keep
 * either result, so a read that cleared would leave one of them empty.
 *
 * @returns {{ term: string, filters: Object|null } | null}
 */
export function useReveal(menu) {
  const [reveal] = useState(() => peekReveal(menu));
  useEffect(() => {
    clearReveal(reveal);
  }, [reveal]);
  return reveal;
}

/**
 * The filters seed for a screen whose search box is `filters[field]` — the
 * handoff's other filter values with the term in the box. null when there is
 * no handoff, which usePersistedState reads as "no seed".
 */
export function revealFilters(reveal, field = 'search') {
  if (!reveal) return null;
  return { ...(reveal.filters || {}), [field]: reveal.term };
}

export default revealInSearch;
